import { Box, Paper, Typography, Chip, Checkbox, Stack } from '@mui/material';
import {
  PersonOutline as PersonIcon,
  Storefront as StorefrontIcon,
  Edit as EditIcon,
  Business as BusinessIcon,
  DevicesOther as DevicesIcon,
} from '@mui/icons-material';
import { Application } from '../types/application';
import STATUS_COLORS from '../../../constants/statusColors';
import { getInflowLabel, getInflowColor } from '../../../constants/inflowChannels';

type Props = {
  app: Application;
  onSelect: () => void;
  selected: boolean;
};

/** 유입 구분 정보 반환 */
const getChannelLabel = (app: Application) => {
  if (app.source_type === 'manual') return { label: '수동', color: '#6366f1', icon: EditIcon };
  if (app.source_type === 'partner') return { label: '거래처', color: '#7c3aed', icon: BusinessIcon };
  if (app.channel === 'b2c') return { label: '개인', color: '#0ea5e9', icon: PersonIcon };
  return { label: '업체', color: '#059669', icon: StorefrontIcon };
};

const OrderCardItem = ({ app, onSelect, selected }: Props) => {
  const formatDevices = (devices: { deviceType: string; deviceModel: string }[]) => {
    if (!devices || devices.length === 0) return '-';
    
    return devices.map((device) => {
      const parts = device.deviceModel.split(' - ');
      
      if (parts.length === 2) {
        const modelName = parts[0];
        const secondPart = parts[1];
        
        // 구성 정보인지 확인
        const configKeywords = ['풀세트', '유닛', '본체', '기타'];
        const isConfig = configKeywords.some(keyword => secondPart.includes(keyword));
        
        if (isConfig) {
          return `${device.deviceType} (${modelName}-${secondPart})`;
        }
      }
      
      // 기존 방식 또는 단순한 경우
      return `${device.deviceType} (${device.deviceModel})`;
    }).join(', ');
  };

  return (
    <Paper
      elevation={selected ? 4 : 0}
      onClick={onSelect}
      sx={{
        p: 2.2,
        mb: 2.2,
        border: selected
          ? '2.5px solid #1976d2'
          : '1.5px solid #e0e7ef',
        bgcolor: selected ? '#e3f2fd' : '#fbfbfc',
        cursor: 'pointer',
        borderRadius: 3,
        boxShadow: selected ? '0 2px 14px #1976d233' : '0 1px 4px #e0e7ef12',
        transition: 'all 0.15s',
        '&:hover': {
          boxShadow: '0 2px 18px #1976d22a',
          borderColor: '#1976d2',
        },
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.8}>
        <Typography fontWeight={800} fontSize={16} color="#222">
          {app.user_name} <span style={{ color: '#8e98a8', fontWeight: 500 }}>({app.phone_number})</span>
        </Typography>
        <Checkbox
          checked={selected}
          sx={{ p: 0.7 }}
          onClick={(e) => e.stopPropagation()}
          onChange={onSelect}
        />
      </Box>

      {/* 유입 구분 + 기기 수 */}
      {(() => {
        const ch = getChannelLabel(app);
        const IconComp = ch.icon;
        const deviceCount = app.devices?.length ?? 0;
        return (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
            <Chip
              icon={<IconComp sx={{ fontSize: 14 }} />}
              label={ch.label}
              size="small"
              sx={{
                fontWeight: 700, fontSize: 11.5, height: 22,
                color: ch.color, bgcolor: `${ch.color}14`,
                border: `1px solid ${ch.color}30`,
                '& .MuiChip-icon': { color: ch.color, ml: 0.3 },
              }}
            />
            <Chip
              icon={<DevicesIcon sx={{ fontSize: 13 }} />}
              label={`${deviceCount}대`}
              size="small"
              sx={{
                fontWeight: 600, fontSize: 11.5, height: 22,
                color: '#6b7280', bgcolor: 'rgba(0,0,0,0.04)',
                border: '1px solid rgba(0,0,0,0.06)',
                '& .MuiChip-icon': { color: '#9ca3af', ml: 0.3 },
              }}
            />
            {app.inflow?.source && (() => {
              const inflowColor = getInflowColor(app.inflow.source);
              return (
                <Chip
                  label={getInflowLabel(app.inflow.source)}
                  size="small"
                  sx={{
                    fontWeight: 700, fontSize: 11.5, height: 22,
                    color: inflowColor, bgcolor: `${inflowColor}14`,
                    border: `1px solid ${inflowColor}30`,
                  }}
                />
              );
            })()}
          </Stack>
        );
      })()}

      <Typography color="text.secondary" sx={{ mt: 0.5, fontSize: 14.5, fontWeight: 500 }}>
        {formatDevices(app.devices)}
      </Typography>

      <Typography sx={{ mt: 1, fontSize: 15.5 }}>
        <b>{(app.total_final_price ?? app.final_price)?.toLocaleString() ?? '-'}</b>
        <span style={{ color: '#888', fontWeight: 400, fontSize: 14 }}> / 검수: </span>
        <b>{(app.total_price_offer ?? app.price_offer)?.toLocaleString() ?? '-'}</b>
      </Typography>

      <Stack direction="row" spacing={0.5} mt={1.3} alignItems="center">
        <Chip
          label={app.status}
          size="small"
          sx={{
            fontWeight: 700,
            color: '#fff',
            bgcolor: STATUS_COLORS[app.status] || '#888',
            minWidth: 68,
            letterSpacing: '-0.5px',
            fontSize: 13.5,
            border: 'none',
            boxShadow: `0 1px 8px ${STATUS_COLORS[app.status] || '#555'}16`,
            borderRadius: 1.2,
          }}
        />
        {app.is_incomplete && (
          <Chip label="정보 미완성" size="small" sx={{ fontWeight: 600, fontSize: 11, height: 22, color: '#d97706', bgcolor: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)' }} />
        )}
      </Stack>
    </Paper>
  );
};

export default OrderCardItem;
